/**
 * /dashboard/accounting — Accounting Overview hub (data dashboard).
 *
 * Pattern E hub landing. The shared AccountingModuleShell renders the
 * horizontal ModuleNav strip (Overview · Sales · Purchases · Money ·
 * Contacts · Items · Reports · Returns · Opening balances · Period locks
 * · Audit) — so the strip ALREADY advertises every destination. The old
 * overview duplicated that strip as 10 static tiles and surfaced zero
 * information. This version leads with live KPI cards (the headline
 * MSME-overdue differentiator first) and drops the redundant tile grid
 * down to a compact "Jump to" link row.
 *
 * Data sources (frontend-only, compose existing hooks):
 *   - useAccountingDashboardWidget — ONE cheap aggregate call backing
 *     receivables / payables / cash / net-profit-30d / unreconciled
 *     receipts / pending-tax-returns / MSME count + outstanding.
 *   - useMSMEOverdueReport — only for the "oldest overdue" detail line
 *     under the MSME card (the aggregate widget has count + outstanding
 *     but not the oldest due date). Self-hides on error / feature-gate.
 *   - usePeriodLocks — latest closed period for the period-lock KPI.
 *
 * No backend endpoint was added. Each KPI block renders its own
 * loading (Skeleton) / empty / error (Alert) state and a 4xx /
 * feature-gated query degrades to a neutral state rather than crashing.
 *
 * Migration note (Phase 3e — 2026-06-01 → dashboard rework 2026-06-15):
 * port of frontend/src/app/(dashboard)/dashboard/accounting/page.tsx with
 * the old Pattern-E `<ModuleShell>` rail folded into AccountingModuleShell.
 */

import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowRight,
  ArrowUpRight,
  ClipboardList,
  FileBarChart,
  Lock,
  ReceiptIndianRupee,
  TrendingUp,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { GstStateBanner } from "@/components/accounting/gst-state-banner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { formatPaise } from "@/lib/expense-format";
import { formatDate } from "@/lib/format";
import { toApiError } from "@/lib/api";
import {
  useAccountingDashboardWidget,
  useMSMEOverdueReport,
  usePeriodLocks,
} from "@/hooks/use-accounting";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/accounting/")({
  component: AccountingOverviewRoute,
});

/**
 * Compact KPI tile. Mirrors the Stat pattern used elsewhere (loans /
 * expenses) but kept module-local on purpose — per the brief we do NOT
 * add a new file under components/shared while sibling modules are being
 * edited in parallel. Renders its own loading / error / value state.
 */
function StatCard({
  icon: Icon,
  label,
  loading,
  error,
  value,
  sub,
  tone = "default",
}: Readonly<{
  icon: LucideIcon;
  label: string;
  loading: boolean;
  error: string | null;
  value: ReactNode;
  sub?: ReactNode;
  tone?: "default" | "warning";
}>) {
  const warn = tone === "warning";
  return (
    <Card className={warn ? "border-destructive/40" : undefined}>
      <CardContent className="flex flex-col gap-2 py-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-muted-foreground">
            {label}
          </span>
          <span
            className={`flex h-8 w-8 items-center justify-center rounded-lg ${
              warn
                ? "bg-destructive/10 text-destructive"
                : "bg-accent text-accent-foreground"
            }`}
          >
            <Icon className="h-4 w-4" />
          </span>
        </div>
        <StatValue loading={loading} error={error} value={value} />
        {!loading && !error && sub ? (
          <span className="text-xs text-muted-foreground">{sub}</span>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Loading / error / value tri-state for a StatCard, extracted to keep
 *  the nesting flat (a feature-gated / 4xx KPI shows a neutral dash). */
function StatValue({
  loading,
  error,
  value,
}: Readonly<{ loading: boolean; error: string | null; value: ReactNode }>) {
  if (loading) return <Skeleton className="h-7 w-28" />;
  if (error)
    return (
      <span
        className="text-2xl font-semibold tabular-nums text-muted-foreground"
        title={error}
      >
        —
      </span>
    );
  return <span className="text-2xl font-semibold tabular-nums">{value}</span>;
}

interface JumpLink {
  label: string;
  href: string;
  icon: LucideIcon;
  blurb: string;
}

/**
 * The strip already covers full navigation — this is the short list of
 * the most-used operational sub-pages, NOT a duplicate of every tile.
 */
const JUMP_LINKS: readonly JumpLink[] = [
  {
    label: "Sales",
    href: "/app/$tenantSlug/accounting/sales",
    icon: ReceiptIndianRupee,
    blurb: "Invoices & quotes",
  },
  {
    label: "Purchases",
    href: "/app/$tenantSlug/accounting/purchases",
    icon: ClipboardList,
    blurb: "Vendor bills",
  },
  {
    label: "Banking",
    href: "/app/$tenantSlug/accounting/money",
    icon: Wallet,
    blurb: "Receipts & payments",
  },
  {
    label: "Financial Reports",
    href: "/app/$tenantSlug/accounting/reports",
    icon: FileBarChart,
    blurb: "TB · P&L · MSME",
  },
] as const;

/**
 * Headline MSME-overdue banner — the differentiator KPI. Renders its own
 * loading / error / clear / overdue state so the route stays flat. A
 * 4xx / feature-gated aggregate degrades to a neutral dash.
 */
function MsmeHeadline({
  tenantSlug,
  loading,
  error,
  count,
  outstandingMinor,
  oldestVendor,
  oldestDaysOverdue,
}: Readonly<{
  tenantSlug: string;
  loading: boolean;
  error: string | null;
  count: number;
  outstandingMinor: number;
  oldestVendor: string | null;
  oldestDaysOverdue: number | null;
}>) {
  const ready = !loading && !error;
  const hasOverdue = ready && count > 0;

  let value: ReactNode;
  if (loading) {
    value = <Skeleton className="mt-1 h-7 w-40" />;
  } else if (error) {
    value = (
      <p className="mt-1 text-2xl font-semibold text-muted-foreground">—</p>
    );
  } else if (count === 0) {
    value = (
      <p className="mt-1 text-2xl font-semibold text-emerald-600 dark:text-emerald-400">
        All clear
      </p>
    );
  } else {
    value = (
      <p className="mt-1 text-2xl font-semibold tabular-nums">
        {count} bill{count === 1 ? "" : "s"} · {formatPaise(outstandingMinor)}
      </p>
    );
  }

  let sub: ReactNode = null;
  if (hasOverdue && oldestVendor !== null && oldestDaysOverdue !== null) {
    sub = (
      <p className="mt-1 text-xs text-muted-foreground">
        Oldest: {oldestVendor} · {oldestDaysOverdue} days past the 45-day window
      </p>
    );
  } else if (ready && count === 0) {
    sub = (
      <p className="mt-1 text-xs text-muted-foreground">
        No supplier bills past the 45-day window.
      </p>
    );
  }

  return (
    <Card className={`mt-4 ${hasOverdue ? "border-destructive/40" : ""}`}>
      <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span
            className={`flex h-10 w-10 items-center justify-center rounded-lg ${
              hasOverdue
                ? "bg-destructive/10 text-destructive"
                : "bg-accent text-accent-foreground"
            }`}
          >
            <AlertTriangle className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-medium text-muted-foreground">
              MSME bills overdue (MSMED Act §15)
            </p>
            {value}
            {sub}
          </div>
        </div>
        <Button
          asChild
          variant={hasOverdue ? "default" : "outline"}
          className="gap-1.5"
        >
          <Link
            to="/app/$tenantSlug/accounting/reports/msme-overdue"
            params={{ tenantSlug }}
          >
            View Report
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function AccountingOverviewRoute() {
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };

  // One cheap aggregate call backs most KPIs.
  const widgetQ = useAccountingDashboardWidget();
  const widget = widgetQ.data ?? null;
  const widgetLoading = widgetQ.isPending;
  const widgetError = widgetQ.isError
    ? toApiError(widgetQ.error).message
    : null;

  // Period-lock status (latest closed period). Independent query.
  const locksQ = usePeriodLocks();
  const locksLoading = locksQ.isPending;
  const locksError = locksQ.isError ? toApiError(locksQ.error).message : null;
  const latestLock =
    locksQ.data?.items && locksQ.data.items.length > 0
      ? // period_end is a YYYY-MM-DD string → lexicographic max == latest.
        locksQ.data.items.reduce((a, b) =>
          a.period_end >= b.period_end ? a : b,
        )
      : null;

  // MSME oldest-overdue detail. The aggregate gives count + outstanding;
  // this fills in the oldest due date for the headline card's sub-line.
  // Self-hides on error so a feature/role 4xx never breaks the page.
  const msmeQ = useMSMEOverdueReport();
  const oldestMSME =
    msmeQ.data?.rows && msmeQ.data.rows.length > 0
      ? msmeQ.data.rows.reduce((a, b) =>
          a.days_overdue >= b.days_overdue ? a : b,
        )
      : null;

  return (
    <AccountingModuleShell
      title="Summary"
      description="Live position across sales, purchases, money and statutory close."
    >
      <div className="mb-4">
        <GstStateBanner />
      </div>
      {widgetError ? (
        <Alert variant="destructive" className="mt-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Could not load accounting summary</AlertTitle>
          <AlertDescription>{widgetError}</AlertDescription>
        </Alert>
      ) : null}

      <MsmeHeadline
        tenantSlug={tenantSlug}
        loading={widgetLoading}
        error={widgetError}
        count={widget?.msme_overdue_count ?? 0}
        outstandingMinor={widget?.msme_overdue_outstanding_minor ?? 0}
        oldestVendor={oldestMSME?.vendor_name ?? null}
        oldestDaysOverdue={oldestMSME?.days_overdue ?? null}
      />

      {/* KPI grid — receivables / payables / cash / profit / unreconciled
          / pending returns. All from the single aggregate widget call. */}
      <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard
          icon={ArrowDownLeft}
          label="Receivables (AR)"
          loading={widgetLoading}
          error={widgetError}
          value={formatPaise(widget?.accounts_receivable_minor ?? 0)}
          sub="Owed to you by customers"
        />
        <StatCard
          icon={ArrowUpRight}
          label="Payables (AP)"
          loading={widgetLoading}
          error={widgetError}
          value={formatPaise(widget?.accounts_payable_minor ?? 0)}
          sub="Owed by you to suppliers"
        />
        <StatCard
          icon={Wallet}
          label="Cash on Hand"
          loading={widgetLoading}
          error={widgetError}
          value={formatPaise(widget?.cash_on_hand_minor ?? 0)}
          sub="Across all payment accounts"
        />
        <StatCard
          icon={TrendingUp}
          label="Net Profit (30d)"
          loading={widgetLoading}
          error={widgetError}
          value={formatPaise(widget?.profit_30d_minor ?? 0)}
          sub="Revenue minus expenses, last 30 days"
        />
        <StatCard
          icon={ReceiptIndianRupee}
          label="Draft Receipts"
          loading={widgetLoading}
          error={widgetError}
          value={(widget?.unreconciled_receipts_count ?? 0).toLocaleString(
            "en-IN",
          )}
          sub="Recorded but not yet posted to the books"
          tone={
            !widgetLoading &&
            !widgetError &&
            (widget?.unreconciled_receipts_count ?? 0) > 0
              ? "warning"
              : "default"
          }
        />
        <StatCard
          icon={FileBarChart}
          label="Pending Tax Returns"
          loading={widgetLoading}
          error={widgetError}
          value={(widget?.pending_tax_returns_count ?? 0).toLocaleString(
            "en-IN",
          )}
          sub="GST / TDS snapshots not yet filed"
          tone={
            !widgetLoading &&
            !widgetError &&
            (widget?.pending_tax_returns_count ?? 0) > 0
              ? "warning"
              : "default"
          }
        />
        <StatCard
          icon={Lock}
          label="Books Closed Through"
          loading={locksLoading}
          error={locksError}
          value={
            latestLock ? (
              formatDate(latestLock.period_end)
            ) : (
              <span className="text-muted-foreground">No periods locked</span>
            )
          }
          sub={
            latestLock
              ? `Locked by ${latestLock.locked_by_name}`
              : "Lock a period from Period locks"
          }
        />
      </div>

      {/* Compact "Jump to" row — the strip handles full nav; these are the
          most-used operational surfaces only (not a duplicate tile grid). */}
      <div className="mt-6">
        <p className="mb-2 text-sm font-medium text-muted-foreground">
          Jump to
        </p>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {JUMP_LINKS.map((j) => {
            const Icon = j.icon;
            return (
              <Link
                key={j.href}
                to={j.href}
                params={{ tenantSlug }}
                className="group"
              >
                <Card className="transition-colors group-hover:border-primary/40">
                  <CardHeader className="flex-row items-center gap-3 space-y-0 py-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                      <Icon className="h-4.5 w-4.5" />
                    </span>
                    <div className="min-w-0">
                      <CardTitle className="text-sm">{j.label}</CardTitle>
                      <CardDescription className="text-xs">
                        {j.blurb}
                      </CardDescription>
                    </div>
                    <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                  </CardHeader>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </AccountingModuleShell>
  );
}
